import { screeningsRoutes } from '@/backend/routes';
import { BackgroundCheckUserPanelHeader } from '@/ui/features/background-check/components/background-check-user-panel/components/BackgroundCheckUserPanelHeader';
import { useBackgroundCheckExpiringOnlyFilter } from '@/ui/features/background-check/hooks/useBackgroundCheckExpiringOnlyFilter';
import { useBackgroundCheckPanelColumns } from '@/ui/features/background-check/hooks/useBackgroundCheckTableColumns';
import { useBackgroundCheckTableFilterConfig } from '@/ui/features/background-check/hooks/useBackgroundCheckTableFilterConfig';
import { ParticipantDetailsComponent } from '@/ui/features/missions';
import { useSlideout } from '@/ui/hooks';
import { DataTable, useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import {
    allowCreateGroup,
    requireAnyOrgPermission,
    requireAnySystemPermission,
} from '@juvare/contact-management/backend/features/permissions/shared';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import { Box, FormControlLabel, Switch } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useSearchParams } from 'react-router';

import { BackgroundCheckColumns, backgroundCheckEntityName } from '@/backend/features/background-check/shared';
import { getEnabledBackgroundCheckTypes } from '@/backend/features/background-check/shared/getEnabledBackgroundCheckTypes';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { mapBackgroundCheckToAccountFilter } from '@/ui/features/background-check/utils/mapBackgroundCheckToAccountFilter';
import { mapBackgroundCheckToFilter } from '@/ui/features/background-check/utils/mapBackgroundCheckToFilter';
import { ExtendedAccountFilterData } from '@/ui/features/contact-management/AccountsPanel/types';
import { useAccountStorage, useGroupStorage } from '@juvare/contact-management/AppClients';
import { AccountColumns } from '@juvare/contact-management/backend/features/accounts/shared';
import { useResolvedAccountFilter } from '@juvare/contact-management/ui/features/accounts/hooks';
import { useDefaultAdvancedSearchItems } from '@juvare/contact-management/ui/features/accounts/hooks/useDefaultAdvancedSearchItems';
import {
    handleAddToExistingGroup,
    handleAddToNewGroup,
    handleAddToOrganization,
} from '@juvare/contact-management/ui/features/organizations/components/OrganizationsPanel/utils';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useEntityDataTable } from '@juvare/platform-sdk-react/ui/hooks';
import { Filter, FilterIdSubFilter } from '@juvare/platform-shared/features/storage';
import isEqual from 'lodash/isEqual';
import { useBackgroundCheckExport } from '../../hooks/useBackgroundCheckExport';

export interface BackgroundCheckUserPanelProps {
    navigateBackTitle: string;
    onNavigateBack: () => void;
}

export const BackgroundCheckUserPanel: React.FC<BackgroundCheckUserPanelProps> = ({ navigateBackTitle, onNavigateBack }) => {
    const { t } = useTranslation();
    const { t: tContactManagement } = useTranslation('contact-management');
    const { state } = useLocation();
    const userState = useSelector(getUserState);
    const settings = useSelector(getSettings);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filter, setFilter] = React.useState<ExtendedAccountFilterData>({});
    const { handlePersonIdSelect, selectedPersonId, menuItems } = useSlideout();
    const requireOrgRoleInStatusFilter = !requireAnySystemPermission(userState, 'all-organizations-admin');
    const groupStorageClient = useGroupStorage();
    const accountStorageClient = useAccountStorage();
    const { backgroundCheckConfig } = useSelector(getSettings);

    const { canEnableExpiringOnlyFilter, isExpiringOnlyFilterChecked, expiringOnlyFilters, handleShowExpiringOnlyFilterChange } =
        useBackgroundCheckExpiringOnlyFilter();

    const defaultAdvancedSearchItems = useDefaultAdvancedSearchItems();
    const accountFilters = useResolvedAccountFilter({
        advancedSearchItems: defaultAdvancedSearchItems,
        filterData: filter,
    });

    const enabledTypes: BackgroundCheckType[] = getEnabledBackgroundCheckTypes(backgroundCheckConfig);

    const numberOfEnabledTypes = enabledTypes?.length ?? 0;

    const filterConfig = useBackgroundCheckTableFilterConfig(requireOrgRoleInStatusFilter, numberOfEnabledTypes);

    const { dataTableProps, reload, clearActiveRow, appliedFilterData } = useEntityDataTable(
        {
            title: t('pages.backgroundCheck.backgroundCheckTableTitle'),
            entityName: backgroundCheckEntityName,
            mapEntityFilterData: (filterData: ExtendedAccountFilterData) => {
                return mapBackgroundCheckToFilter({
                    enabledTypes: enabledTypes,
                    filterData: filterData,
                    expiringOnlyFilters: expiringOnlyFilters,
                    userState: userState,
                    accountFilters: accountFilters?.data,
                }).filter;
            },
            filterConfig,
            columns: [],
            defaultSortColumnKey: 'updatedAt',
        },
        {
            useDataTableFunction: useDataTableUsingUrl,
            onRowClick: () => undefined,
            rowSelectionEnabled: true,
        },
    );

    const backgroundCheckOrderBy = dataTableProps.sortColumnKey
        ? [
              {
                  column: dataTableProps.sortColumnKey as keyof BackgroundCheckColumns,
                  desc: Boolean(dataTableProps.sortColumnDesc),
              },
          ]
        : [];

    React.useEffect(() => {
        if (!isEqual(appliedFilterData, filter)) {
            setFilter(appliedFilterData);
        }
    }, [appliedFilterData]);

    const { handleExportBackgroundCheck: handleExport } = useBackgroundCheckExport({
        filter:
            dataTableProps.selectedRowIds && dataTableProps.selectedRowIds.length > 0
                ? [
                      {
                          id: dataTableProps.selectedRowIds,
                          op: 'in',
                      },
                  ]
                : mapBackgroundCheckToFilter({
                      enabledTypes: enabledTypes,
                      filterData: appliedFilterData,
                      expiringOnlyFilters: expiringOnlyFilters,
                      userState: userState,
                      accountFilters: accountFilters?.data,
                  }).filter,
        orderBy: backgroundCheckOrderBy,
        numberOfActiveBackgroundCheckTypes: numberOfEnabledTypes,
        backgroundCheckConfig,
    });
    const { handleExportBackgroundCheck: handleExportAll } = useBackgroundCheckExport({
        filter: mapBackgroundCheckToFilter({
            enabledTypes: enabledTypes,
            filterData: appliedFilterData,
            expiringOnlyFilters: expiringOnlyFilters,
            userState: userState,
            accountFilters: accountFilters?.data,
        }).defaultFilters,
        numberOfActiveBackgroundCheckTypes: numberOfEnabledTypes,
        backgroundCheckConfig,
        orderBy: backgroundCheckOrderBy,
    });

    const id = searchParams.get('id');
    React.useEffect(() => {
        if (!selectedPersonId && id && !!dataTableProps.rows.length) {
            // Check if it's a row id or personId
            const idToSave = dataTableProps.rows.find((rowId) => rowId.id === id)?.columns?.accountId ?? id;

            handlePersonIdSelect(idToSave);
        }
    }, [handlePersonIdSelect, id, selectedPersonId, dataTableProps.rows]);

    const handleClearPersonId = () => {
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                params.delete('id');
                return params;
            },
            { replace: true },
        );

        clearActiveRow();
    };

    const getAccountIdsFromRowIds = async (selectedRows?: string[]) => {
        if (!selectedRows) {
            return [];
        }

        const accounts = await accountStorageClient.list({
            filter: [
                {
                    op: 'in',
                    idSubFilter: {
                        entity: backgroundCheckEntityName,
                        column: 'accountId',
                        filter: [
                            {
                                id: selectedRows,
                                op: 'in',
                            },
                        ],
                    } satisfies FilterIdSubFilter<BackgroundCheckColumns>['idSubFilter'],
                },
            ],
        });

        return accounts.rows.map(({ id }) => id);
    };

    dataTableProps.columns = useBackgroundCheckPanelColumns({
        reload,
        numberOfActiveBackgroundCheckTypes: numberOfEnabledTypes,
    });

    if (!state?.backgroundCheckType) {
        return <Navigate to={`/${screeningsRoutes.root}`} replace />;
    }

    const rowCount =
        dataTableProps.selectedRowIds && dataTableProps.selectedRowIds.length > 0
            ? dataTableProps.selectedRowIds.length
            : (dataTableProps.filteredRowCount ?? 0);

    return (
        <>
            <BackgroundCheckUserPanelHeader
                backgroundCheckType={state.backgroundCheckType}
                navigateBackTitle={navigateBackTitle}
                onNavigateBack={onNavigateBack}
            />
            <Box
                sx={{
                    p: 2,
                }}
            >
                <DataTable
                    {...dataTableProps}
                    data-test='background-check-table'
                    customHeaderElement={
                        canEnableExpiringOnlyFilter && (
                            <FormControlLabel
                                sx={{ mt: -2, mb: -2 }}
                                label={t('pages.backgroundCheck.showExpiredAndExpiringOnly')}
                                control={<Switch checked={isExpiringOnlyFilterChecked} onChange={handleShowExpiringOnlyFilterChange} />}
                            />
                        )
                    }
                    globalActions={[
                        {
                            icon: <FileUploadOutlinedIcon />,
                            label: t('common.exportAll'),
                            onClick: handleExportAll,
                            'data-test': 'export-global-action-button',
                        },
                    ]}
                    localActions={[
                        ...(allowCreateGroup(userState)
                            ? [
                                  {
                                      icon: <GroupAddOutlinedIcon />,
                                      label: tContactManagement('panels.accounts.addToGroup'),
                                      'data-test': 'add-to-group-button',
                                      disabled: rowCount === 0,
                                      menuItems: [
                                          {
                                              label: tContactManagement('panels.accounts.newGroup'),
                                              onClick: async () =>
                                                  handleAddToNewGroup({
                                                      t: tContactManagement,
                                                      accountIds: await getAccountIdsFromRowIds(dataTableProps?.selectedRowIds),
                                                      totalFilteredAccounts: dataTableProps.filteredRowCount,
                                                      appliedStorageFilters: mapBackgroundCheckToAccountFilter(
                                                          enabledTypes,
                                                          appliedFilterData,
                                                          expiringOnlyFilters,
                                                          accountFilters?.data,
                                                      ).filter as Filter<AccountColumns>[],
                                                      groupStorageClient,
                                                      onClose: () => {
                                                          dataTableProps.onDeselectAllRows?.();
                                                          reload();
                                                      },
                                                  }),
                                              'data-test': 'new-group-button',
                                          },
                                          {
                                              label: tContactManagement('panels.accounts.addToExistingGroup'),
                                              onClick: async () =>
                                                  handleAddToExistingGroup({
                                                      t: tContactManagement,
                                                      accountIds: await getAccountIdsFromRowIds(dataTableProps?.selectedRowIds),
                                                      appliedStorageFilters: mapBackgroundCheckToAccountFilter(
                                                          enabledTypes,
                                                          appliedFilterData,
                                                          expiringOnlyFilters,
                                                          accountFilters?.data,
                                                      ).filter as Filter<AccountColumns>[],
                                                      onClose: () => {
                                                          dataTableProps.onDeselectAllRows?.();
                                                          reload();
                                                      },
                                                  }),
                                              'data-test': 'existing-group-button',
                                          },
                                      ],
                                  },
                              ]
                            : []),
                        ...(settings.organizationsEnabled &&
                        (requireAnySystemPermission(userState, 'manage-accounts') || requireAnyOrgPermission(userState, 'manage-members'))
                            ? [
                                  {
                                      label: tContactManagement('panels.accounts.addToOrganization'),
                                      icon: <DomainAddOutlinedIcon />,
                                      'data-test': 'toolbar-add-to-organization-button',
                                      onClick: async () =>
                                          handleAddToOrganization({
                                              t: tContactManagement,
                                              accountIds: await getAccountIdsFromRowIds(dataTableProps?.selectedRowIds),
                                              totalFilteredAccounts: dataTableProps?.filteredRowCount,
                                              appliedStorageFilters: mapBackgroundCheckToAccountFilter(
                                                  enabledTypes,
                                                  appliedFilterData,
                                                  expiringOnlyFilters,
                                                  accountFilters?.data,
                                              ).filter as Filter<AccountColumns>[],
                                              onConfirm: () => {
                                                  dataTableProps.onDeselectAllRows?.();
                                                  reload();
                                              },
                                          }),
                                  },
                              ]
                            : []),
                        {
                            icon: <FileUploadOutlinedIcon />,
                            label: t('common.export'),
                            onClick: handleExport,
                            'data-test': 'export-button',
                        },
                    ]}
                />
            </Box>
            <ParticipantDetailsComponent
                participantId={selectedPersonId}
                onParticipantIdReset={handleClearPersonId}
                slideoutMenuItems={menuItems}
            />
        </>
    );
};
